import React, { Dispatch, FC, memo, SetStateAction, useEffect, useRef, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import styles from './google-maps-stores.module.css';
import { AdvancedMarker, useMap } from '@vis.gl/react-google-maps';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import type { Marker } from '@googlemaps/markerclusterer';
import { StoreSearchViewModel } from '~/lib/data-contract';
import { MediaImage } from '~/shared/components';

const clusterRenderer = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    render: (cluster: any) => {
        const count = cluster.count;
        const position = cluster.position;
        const div = document.createElement('div');
        const htmlString = ReactDOMServer.renderToString(<ClusterElement count={count} />);
        div.innerHTML = htmlString;
        return new google.maps.marker.AdvancedMarkerElement({
            position: { lat: position.lat(), lng: position.lng() },
            content: div,
        }) as Marker;
    },
};

type CustomMarkerProps = {
    stores: StoreSearchViewModel[];
    setSelectedStore: Dispatch<SetStateAction<StoreSearchViewModel | null>>;
};

const Markers: FC<CustomMarkerProps> = memo(({ stores, setSelectedStore }) => {
    const map = useMap();
    const markerRefs = useRef<{ [key: string]: Marker | null }>({});
    const clustererRef = useRef<MarkerClusterer | null>(null);
    const [markersReady, setMarkersReady] = useState(false);

    // Initialize clusterer when map is ready
    useEffect(() => {
        if (map && !clustererRef.current) {
            clustererRef.current = new MarkerClusterer({ map, renderer: clusterRenderer });
        }
    }, [map]);

    // Track when all marker refs are set
    useEffect(() => {
        if (stores.length === 0) {
            setMarkersReady(false);
            return;
        }
        const allSet = stores.every((store) => markerRefs.current[store.id || '']);
        setMarkersReady(allSet);
    }, [stores]);

    // Update clusterer when markers are ready
    useEffect(() => {
        if (!clustererRef.current) return;
        if (!markersReady) return;
        const markersArray = Object.values(markerRefs.current).filter(Boolean);
        clustererRef.current.clearMarkers();
        clustererRef.current.addMarkers(markersArray as Marker[]);
    }, [markersReady, stores]);

    // Clear clusterer when stores change and there are no stores
    useEffect(() => {
        if (!clustererRef.current) return;
        if (stores.length === 0) {
            clustererRef.current.clearMarkers();
        }
    }, [stores]);

    return (
        <>
            {stores.map((store) => {
                const markerPosition = { lat: store.latitude || 0, lng: store.longitude || 0 };
                return (
                    <AdvancedMarker
                        key={store.id}
                        position={markerPosition}
                        onClick={() => setSelectedStore(store)}
                        ref={(marker) => {
                            markerRefs.current[store.id || ''] = marker;
                            // Check if all markers are set after each ref update
                            const allSet = stores.every((s) => markerRefs.current[s.id || '']);
                            if (allSet) setMarkersReady(true);
                        }}
                    >
                        {/* <Pin
                            background="black"
                            borderColor="white"
                            glyphColor="white"
                            scale={0.7}
                        /> */}

                        <MediaImage
                            src="/assets/icons/google-maps/map-pin.svg"
                            alt={`${store.storeName} location`}
                            width={30}
                            height={30}
                        />
                    </AdvancedMarker>
                );
            })}
        </>
    );
});

const ClusterElement = memo(({ count }: { count: number }) => {
    return (
        <div className={styles.cluster}>
            <span>{count}</span>
        </div>
    );
});

Markers.displayName = 'GoogleMapsMarkers';
export default Markers;
